// FIX-CHART: Chart properties panel.
// Allows editing ChartType, Mode, Title, colors, bound/static data,
// DataSource/CategoryField/ValueField bindings, and display options.

import React, {Component} from 'react';
import {connect} from 'react-redux';
import objeler from '../objeler';
import {changePropertiesById2} from '../../redux/actions';

class ChartProperties extends Component {
    constructor(props) {
        super(props);
        this.state = {
            forceUpdate: 0
        };
    }

    // Helper: find the active Chart element from props/Redux
    getElement() {
        const {activeElement, objects} = this.props;

        // 1. First try Redux activeElement (if selected on canvas)
        let el = null;
        if (activeElement) {
            const id = activeElement.id || activeElement;
            if (typeof activeElement === 'object' && activeElement.properties) {
                el = activeElement;
            } else if (id && objects && objects[id]) {
                el = objects[id];
            }
        }

        // 2. Fallback: modal mode uses objeler.ElementProps.Object (set by Draggable doubleClick)
        if (!el && objeler.ElementProps && objeler.ElementProps.Object) {
            el = objeler.ElementProps.Object;
        }

        // Only return if the element is actually a Chart
        if (el && el.type !== 'Chart') return null;
        return el;
    }

    // Update a single property and propagate through Redux
    updateProp(key, value) {
        const el = this.getElement();
        if (!el) return;

        const newProps = {...(el.properties || {}), [key]: value};
        // also mirror to objeler.ElementProps in case Container uses it
        if (objeler.ElementProps) objeler.ElementProps[key] = value;

        this.props.changePropertiesById2(el.id || el.ObjName, {
            properties: newProps
        });
        this.setState({forceUpdate: this.state.forceUpdate + 1});
    }

    // ChartData — list of {label, value, color} rows.
    // Convenience: add / remove / edit row.
    addDataRow() {
        const el = this.getElement();
        if (!el) return;
        const cd = [...(el.properties?.ChartData || [])];
        cd.push({label: `Item ${cd.length + 1}`, value: 0, color: ''});
        this.updateProp('ChartData', cd);
    }

    removeDataRow(idx) {
        const el = this.getElement();
        if (!el) return;
        const cd = [...(el.properties?.ChartData || [])];
        cd.splice(idx, 1);
        this.updateProp('ChartData', cd);
    }

    updateDataRow(idx, field, value) {
        const el = this.getElement();
        if (!el) return;
        const cd = [...(el.properties?.ChartData || [])];
        if (!cd[idx]) return;
        cd[idx] = {...cd[idx], [field]: field === 'value' ? Number(value) || 0 : value};
        this.updateProp('ChartData', cd);
    }

    renderStaticDataEditor() {
        const el = this.getElement();
        if (!el) return null;
        const data = el.properties?.ChartData || [];

        return (
            <div style={{marginTop: 8, border: '1px solid #ddd', padding: 6, background: '#fafafa'}}>
                <div style={{fontWeight: 'bold', marginBottom: 4, fontSize: 12}}>
                    Chart Data ({data.length} point{data.length !== 1 ? 's' : ''})
                </div>
                <table style={{width: '100%', fontSize: 11}}>
                    <thead>
                        <tr style={{background: '#eee'}}>
                            <th style={{padding: '2px 4px', textAlign: 'left'}}>Label</th>
                            <th style={{padding: '2px 4px', textAlign: 'left', width: 70}}>Value</th>
                            <th style={{padding: '2px 4px', textAlign: 'left', width: 70}}>Color</th>
                            <th style={{width: 22}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, i) => (
                            <tr key={i}>
                                <td style={{padding: '2px 4px'}}>
                                    <input type="text"
                                           style={{width: '100%', fontSize: 11, border: '1px solid #ccc', padding: '1px 3px'}}
                                           value={row.label || ''}
                                           onChange={e => this.updateDataRow(i, 'label', e.target.value)}/>
                                </td>
                                <td style={{padding: '2px 4px'}}>
                                    <input type="number"
                                           style={{width: 60, fontSize: 11, border: '1px solid #ccc', padding: '1px 3px'}}
                                           value={row.value ?? 0}
                                           onChange={e => this.updateDataRow(i, 'value', e.target.value)}/>
                                </td>
                                <td style={{padding: '2px 4px'}}>
                                    <input type="text"
                                           placeholder="#RRGGBB"
                                           style={{width: 60, fontSize: 11, border: '1px solid #ccc', padding: '1px 3px'}}
                                           value={row.color || ''}
                                           onChange={e => this.updateDataRow(i, 'color', e.target.value)}/>
                                </td>
                                <td style={{padding: '2px 4px', textAlign: 'center'}}>
                                    <button style={{
                                        border: 'none',
                                        background: '#f66',
                                        color: '#fff',
                                        width: 18,
                                        height: 18,
                                        fontSize: 11,
                                        cursor: 'pointer',
                                        borderRadius: 2
                                    }}
                                            onClick={() => this.removeDataRow(i)}
                                            title="Remove row">×</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button style={{
                    marginTop: 4,
                    fontSize: 11,
                    padding: '3px 10px',
                    border: '1px solid #3182CE',
                    background: '#3182CE',
                    color: '#fff',
                    cursor: 'pointer',
                    borderRadius: 2
                }}
                        onClick={() => this.addDataRow()}>+ Add Row</button>
            </div>
        );
    }

    // FIX-CHART v2: Extract DataSource items from objeler.ModalDocParameters (same pattern as TableProperties).
    // Reads Document Properties > DataSource array — NOT Redux state.Datasource (which is for save/load).
    getDataSourceItems() {
        if (!objeler.ModalDocParameters || !Array.isArray(objeler.ModalDocParameters.DataSource)) {
            return [];
        }

        return objeler.ModalDocParameters.DataSource.map(ds => {
            const name = ds.title || ds.Title || 'Unnamed';
            let fields = [];

            // PRIORITY 1: Parse from query (most reliable for chart category/value field names)
            if (ds.query && typeof ds.query === 'string' && ds.query.toLowerCase().includes('select')) {
                const m = ds.query.match(/select\s+(.+?)\s+from/i);
                if (m && m[1]) {
                    const names = m[1].split(',').map(k => k.trim());
                    fields = names.map(n => ({Name: n, DataType: 'String'}));
                }
            }

            // PRIORITY 2: rows array (if parsing failed and rows has >2 items, treat as real field list)
            if (fields.length === 0 && ds.rows && Array.isArray(ds.rows) && ds.rows.length > 2) {
                fields = ds.rows.map(row => ({
                    Name: row.Name || row.name || row.value || '',
                    DataType: row.DataType || row.dataType || 'String'
                }));
            }

            // PRIORITY 3: children (nested Fields node)
            if (fields.length === 0 && ds.children && Array.isArray(ds.children)) {
                const fieldsNode = ds.children.find(c =>
                    c.title === 'Fields' || c.Title === 'Fields'
                );
                if (fieldsNode && Array.isArray(fieldsNode.rows)) {
                    fields = fieldsNode.rows.map(row => ({
                        Name: row.Name || row.name || row.value || '',
                        DataType: row.DataType || row.dataType || 'String'
                    }));
                }
            }

            return {Name: name, Fields: fields};
        });
    }

    renderDynamicEditor() {
        const el = this.getElement();
        if (!el) return null;
        const props = el.properties || {};

        const datasources = this.getDataSourceItems();
        const selectedDs = datasources.find(ds => ds.Name === props.DataSource);
        const fields = selectedDs ? selectedDs.Fields : [];

        const noDataSourcesDefined = datasources.length === 0;

        return (
            <div style={{marginTop: 8, border: '1px solid #ddd', padding: 6, background: '#fafafa'}}>
                <div style={{fontWeight: 'bold', marginBottom: 4, fontSize: 12}}>
                    Dynamic Data Binding
                </div>

                {noDataSourcesDefined && (
                    <div style={{
                        padding: 6,
                        background: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: 3,
                        fontSize: 10,
                        color: '#856404',
                        marginBottom: 6
                    }}>
                        ⚠️ Henüz DataSource tanımlanmamış. <br/>
                        File → Data Source üzerinden DataSource ekleyin.
                    </div>
                )}

                <div style={{fontSize: 11, marginBottom: 4}}>
                    <label style={{display: 'block', marginBottom: 2}}>DataSource:</label>
                    <select style={{width: '100%', fontSize: 11, padding: '2px 4px'}}
                            value={props.DataSource || ''}
                            onChange={e => this.updateProp('DataSource', e.target.value)}>
                        <option value="">-- Select --</option>
                        {datasources.map(ds => (
                            <option key={ds.Name} value={ds.Name}>
                                {ds.Name} ({ds.Fields.length} fields)
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{fontSize: 11, marginBottom: 4}}>
                    <label style={{display: 'block', marginBottom: 2}}>Category Field (X):</label>
                    {fields.length > 0 ? (
                        <select style={{width: '100%', fontSize: 11, padding: '2px 4px'}}
                                value={props.CategoryField || ''}
                                onChange={e => this.updateProp('CategoryField', e.target.value)}>
                            <option value="">-- Select --</option>
                            {fields.map(f => (
                                <option key={f.Name} value={f.Name}>{f.Name}</option>
                            ))}
                        </select>
                    ) : (
                        <input type="text"
                               style={{width: '100%', fontSize: 11, border: '1px solid #ccc', padding: '2px 4px'}}
                               placeholder="e.g. ProductService"
                               value={props.CategoryField || ''}
                               onChange={e => this.updateProp('CategoryField', e.target.value)}/>
                    )}
                </div>

                <div style={{fontSize: 11}}>
                    <label style={{display: 'block', marginBottom: 2}}>Value Field (Y):</label>
                    {fields.length > 0 ? (
                        <select style={{width: '100%', fontSize: 11, padding: '2px 4px'}}
                                value={props.ValueField || ''}
                                onChange={e => this.updateProp('ValueField', e.target.value)}>
                            <option value="">-- Select --</option>
                            {fields.map(f => (
                                <option key={f.Name} value={f.Name}>{f.Name}</option>
                            ))}
                        </select>
                    ) : (
                        <input type="text"
                               style={{width: '100%', fontSize: 11, border: '1px solid #ccc', padding: '2px 4px'}}
                               placeholder="e.g. Total"
                               value={props.ValueField || ''}
                               onChange={e => this.updateProp('ValueField', e.target.value)}/>
                    )}
                </div>

                <div style={{marginTop: 6, fontSize: 10, color: '#999', fontStyle: 'italic'}}>
                    PDF render: DataSource sorgusu çalıştırılır, her satır bir chart data point olur.
                </div>
            </div>
        );
    }

    render() {
        const el = this.getElement();
        if (!el) {
            return (
                <div style={{padding: 10, fontSize: 12, color: '#888'}}>
                    Select a Chart element to edit properties.
                </div>
            );
        }
        const props = el.properties || {};
        const mode = props.Mode || 'Manual';

        const labelStyle = {display: 'inline-block', width: 100, fontSize: 12, color: '#333'};
        const inputStyle = {fontSize: 12, padding: '2px 4px', border: '1px solid #ccc', marginBottom: 4};
        const rowStyle = {marginBottom: 6};

        return (
            <div style={{padding: 8, fontFamily: 'Raleway, Arial', fontSize: 12}}>
                <h4 style={{margin: '0 0 10px 0', fontSize: 14, color: '#3182CE'}}>Chart</h4>

                <div style={rowStyle}>
                    <label style={labelStyle}>Chart Type:</label>
                    <select style={inputStyle}
                            value={props.ChartType || 'Bar'}
                            onChange={e => this.updateProp('ChartType', e.target.value)}>
                        <option value="Bar">Bar</option>
                        <option value="Line">Line</option>
                        <option value="Pie">Pie</option>
                        <option value="Donut">Donut</option>
                    </select>
                </div>

                <div style={rowStyle}>
                    <label style={labelStyle}>Mode:</label>
                    <select style={inputStyle}
                            value={mode}
                            onChange={e => this.updateProp('Mode', e.target.value)}>
                        <option value="Manual">Manual (Static)</option>
                        <option value="Dynamic">Dynamic (DataSource)</option>
                    </select>
                </div>

                <div style={rowStyle}>
                    <label style={labelStyle}>Title:</label>
                    <input type="text" style={{...inputStyle, width: 180}}
                           value={props.Title || ''}
                           onChange={e => this.updateProp('Title', e.target.value)}/>
                </div>

                <div style={rowStyle}>
                    <label style={labelStyle}>Bar Color:</label>
                    <input type="color" style={{...inputStyle, width: 40, padding: 0, height: 22}}
                           value={props.BarColor || '#3182CE'}
                           onChange={e => this.updateProp('BarColor', e.target.value)}/>
                    <input type="text" style={{...inputStyle, width: 90, marginLeft: 6}}
                           value={props.BarColor || '#3182CE'}
                           onChange={e => this.updateProp('BarColor', e.target.value)}/>
                </div>

                <fieldset style={{border: '1px solid #ddd', padding: 6, marginTop: 6}}>
                    <legend style={{fontSize: 11, color: '#666', padding: '0 4px'}}>Display Options</legend>
                    <label style={{display: 'block', fontSize: 11, marginBottom: 3}}>
                        <input type="checkbox"
                               checked={props.ShowValues !== false}
                               onChange={e => this.updateProp('ShowValues', e.target.checked)}/>
                        &nbsp;Show values on bars
                    </label>
                    <label style={{display: 'block', fontSize: 11, marginBottom: 3}}>
                        <input type="checkbox"
                               checked={props.ShowGrid !== false}
                               onChange={e => this.updateProp('ShowGrid', e.target.checked)}/>
                        &nbsp;Show grid lines
                    </label>
                    <label style={{display: 'block', fontSize: 11, marginBottom: 3}}>
                        <input type="checkbox"
                               checked={props.ShowAxisLabels !== false}
                               onChange={e => this.updateProp('ShowAxisLabels', e.target.checked)}/>
                        &nbsp;Show axis labels
                    </label>
                    <label style={{display: 'block', fontSize: 11}}>
                        <input type="checkbox"
                               checked={props.ShowLegend === true}
                               onChange={e => this.updateProp('ShowLegend', e.target.checked)}/>
                        &nbsp;Show legend (Pie/Donut)
                    </label>
                </fieldset>

                <div style={{marginTop: 6}}>
                    <label style={labelStyle}>Title Font Size:</label>
                    <input type="number" min="6" max="36" style={{...inputStyle, width: 60}}
                           value={props.TitleFontSize || 12}
                           onChange={e => this.updateProp('TitleFontSize', Number(e.target.value) || 12)}/>
                </div>
                <div style={{marginTop: 4}}>
                    <label style={labelStyle}>Label Font Size:</label>
                    <input type="number" min="6" max="24" style={{...inputStyle, width: 60}}
                           value={props.LabelFontSize || 8}
                           onChange={e => this.updateProp('LabelFontSize', Number(e.target.value) || 8)}/>
                </div>

                {mode === 'Manual' && this.renderStaticDataEditor()}
                {mode === 'Dynamic' && this.renderDynamicEditor()}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    objects: state.appReducers.objects,
    activeElement: state.appReducers.activeElement
    // Note: Datasource list is read from objeler.ModalDocParameters.DataSource, not Redux state.
});

export default connect(mapStateToProps, {changePropertiesById2})(ChartProperties);
